import { config } from "../config";
import { callGateway, type CallerIdentity, type GatewayResult } from "./gateway";

/**
 * radreportsapi, reached through the radaccess gateway at /access/radreport.
 *
 * 🚨 METHOD: POST, not GET.
 *
 * The service registers all 15 report routes as GET (app/reports.go:13-27) and
 * reads the scope from a JSON request BODY (decodeReportFilter,
 * app/handler/reports.go:59 — there is no query-parameter fallback). PHP's curl
 * sends a body on a GET quite happily, which is why the legacy portal works and
 * always has. A browser cannot: the Fetch standard throws a TypeError for a GET
 * with a body.
 *
 * So these calls target POST aliases. Until those aliases ship, every call here
 * returns 405 — which is the honest failure, and better than a client that
 * cannot be written at all.
 */

/**
 * The report scope contract, transcribed field-for-field from radreportsapi's
 * `reportFilter` struct (app/handler/reports.go).
 *
 * 🪤 The first version of this interface was WRITTEN rather than DERIVED, and
 * every name in it was wrong: `namespaceprefix` for `namespace_prefix`,
 * `startdate`/`enddate` for `start`/`end`, plus `limit` and `offset`, which do
 * not exist on the struct at all. Go's encoding/json ignores unknown keys and
 * leaves absent ones at their zero value, so the wrong names did not error —
 * the scope field simply arrived empty and the service refused the request for
 * having no scope. An invented type does not fail loudly; it fails as a
 * confusing message about something else.
 *
 * 🔑 These are wire names and must match the json tags EXACTLY. Do not rename
 * them to look like the rest of this codebase.
 */
export interface ReportFilter {
  /**
   * The tenant subtree the report covers. Supplied from the TOKEN by the
   * caller of these functions — never from the UI.
   */
  namespace_prefix: string;
  namespace?: string;
  netid?: number;
  mobile?: string;
  username?: string;
  primary_user?: string;
  onumac?: string;
  customerid?: string;
  accountid?: string;
  groupuid?: string;
  /** RFC3339 — the Go side decodes these into *time.Time. */
  start?: string;
  end?: string;
  /** Read by /reports/collections only: "month", else per day. */
  group_by?: "day" | "month";
}

/** The wire name of the scope field, pinned so a rename cannot go unnoticed. */
export const SCOPE_FIELD = "namespace_prefix" as const;

const path = (p: string) => `/radreport/reports/${p}`;

async function report<T>(
  identity: CallerIdentity,
  endpoint: string,
  filter: Omit<ReportFilter, "namespace_prefix">,
  signal?: AbortSignal,
): Promise<GatewayResult<T>> {
  if (!identity.namespace) {
    // Belt and braces — callGateway also refuses, but this keeps the scope
    // field from ever being assembled from anything but the token.
    return callGateway<T>(identity, config().gatewayBase, path(endpoint), { method: "POST" });
  }
  const body: ReportFilter = { ...filter, [SCOPE_FIELD]: identity.namespace };
  return callGateway<T>(identity, config().gatewayBase, path(endpoint), {
    method: "POST",
    body,
    ...(signal ? { signal } : {}),
  });
}

/** The 15 report endpoints, as registered live on radreportsapi. */
export const REPORTS = {
  activeSessions: "activesessions",
  userSessions: "usersessions",
  customerSessionsDaily: "customersessions/daily",
  customerSessionsSummary: "customersessions/summary",
  activePlans: "activeplans",
  expiringPlans: "expiringplans",
  plans: "plans",
  planDetail: "plandetail",
  customerDevices: "customerdevices",
  customerIdentity: "customeridentity",
  customerPlan: "customerplan",
  customerRecharges: "customerrecharges",
  rechargeLogAll: "rechargelog/all",
  rechargeLogByUser: "rechargelog/byuser",
  smsLog: "smslog",
  // Subscriber-management reports (owner 2026-10-24; radreportsapi v0.1.34).
  expiredNotRenewed: "expirednotrenewed",
  failedLogins: "failedlogins",
  collections: "collections",
  fundsStatement: "fundsstatement",
  newSubscribers: "newsubscribers",
  // Phase 2 (owner 2026-10-24; radreportsapi v0.1.36).
  renewalsByPlan: "renewalsbyplan",
  neverUsed: "neverused",
  topUsage: "topusage",
  peakHours: "peakhours",
  paymentFailures: "paymentfailures",
  promoUse: "promouse",
  baseSnapshot: "basesnapshot",
  staffActivity: "staffactivity",
  tenantSummary: "tenantsummary",
} as const;

export type ReportRow = Record<string, unknown>;

export function fetchReport(
  identity: CallerIdentity,
  endpoint: string,
  filter: Omit<ReportFilter, "namespace_prefix"> = {},
  signal?: AbortSignal,
): Promise<GatewayResult<ReportRow[]>> {
  return report<ReportRow[]>(identity, endpoint, filter, signal);
}
